import * as React from 'react';
import { Survey } from 'survey-react-ui';
import { Model } from 'survey-core';
import { css } from './../../../Webform';
import { fetchQuizForm, submitForm, updateStepData } from '../../api';
import { Config, onTextMarkdownHandler } from '../../helper';
import { useZustand } from 'use-zustand';
import { pathwayStore } from '../../store';
import hljs from 'highlight.js';
import BrokenStep from './BrokenStep';

function Preview(props) {
  const {
    clickHandler,
    loaded,
    config: {
      label,
      text,
      cta,
    } = {},
  } = props;

  if (loaded) {
    return null;
  }

  return (
    <div className='page__section page__section--light-gray page__cta-section'>
      <h3 className='h--small lead text-center'>{label}</h3>
      <p className='p--large lead text-center text-color-gray mb-4'>{text}</p>
      <p className='text-center mb-0'>
        <button
          className='nv-cta-primary nv-cta-primary--small'
          onClick={() => {
            clickHandler()
          }}>
          {cta}
        </button>
      </p>
    </div>
  );
}

function QuizForm(props) {
  const {
    survey,
    completeCallback,
    formId,
    submission,
    pathwayID,
    step,
    loaded,
    setHasError,
    setErrorText,
  } = props;

  if (!loaded) {
    return null;
  }

  function buildPayload(formId, submissionData) {
    return {
      form_id: formId,
      submission_data: submissionData,
    };
  }

  survey.onComplete.add((sender, options) => {
    updateStepData(pathwayID, step.id, buildPayload(formId, sender.data))
      .then(response => {
        completeCallback();
      })
      .catch(error => {
        console.log(error);
        setErrorText(Config.I18n.Errors.SubmissionFailure)
        setHasError(true);
      });
  });

  // Disable scroll behavior
  survey.scrollElementToTop = function () {
  };
  survey.scrollToQuestion = function () {
  };
  survey.scrollToElement = function () {
  };
  survey.focusFirstQuestionAutomatic = false;
  survey.autoScrollToQuestion = false;

  survey.onAfterRenderQuestion.add(function (survey, options) {
    options.htmlElement.querySelectorAll('pre code').forEach((block) => {
      hljs.configure({
        languages: ['python']
      });
      hljs.highlightElement(block);
    });
  });

  return (
    <div className='quiz-form-wrapper'>
      <Survey model={survey} css={css}/>
    </div>
  );
}

function QuizSummary(props) {
  const {summaryHTML} = props;

  const wrapperEl = React.useRef(null);

  const toggleSize = () => {
    wrapperEl.current.classList.toggle('expanded');
  };

  return (
    <div className='quiz-summary-wrapper' ref={wrapperEl}>
      <div className='quiz-summary' dangerouslySetInnerHTML={{__html: summaryHTML}}></div>
      <button className='quiz-summary-wrapper__toggler' type='button' onClick={toggleSize}>
        {Config.I18n.Action.ReadAll}
      </button>
    </div>
  );
}

export default function Quiz(props) {
  const {step} = props;
  const {config, summary: summaryHTML = null} = step;
  const {formId = null} = config;
  const [loaded, setLoaded] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const [errorText, setErrorText] = React.useState('');
  const [survey, setSurvey] = React.useState(null);
  const [submission, setSubmission] = React.useState(null);

  const {
    updateStep,
    pathwayID,
    stepUserData,
  } = useZustand(pathwayStore, (state) => ({
    pathwayID: state.pathwayID,
    updateStep: state.updateStep,
    stepUserData: state.stepUserData,
  }));

  const userData = stepUserData(step.id);

  const doSetLoaded = () => {
    setLoaded(true);
  }

  React.useEffect(() => {
    fetchQuizForm(formId)
      .then(([data]) => {
        const {submission} = data
        let model = new Model(data);
        if (submission?.id && submission?.form_id) {
          const {data: submissionData} = submission;
          setSubmission(submission);
          model.data = submissionData;
        }

        model.onTextMarkdown.add(onTextMarkdownHandler);

        setSurvey(model);
      }).catch(error => {
      setHasError(true);
    });
  }, []);

  React.useEffect(() => {
    if (userData) {
      const {submission_id = null} = userData;
      if (submission_id) {
        setSubmission(submission_id);
      }
    }
  }, [userData]);

  React.useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = CSS.highlightCSS;
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const completeStep = () => {
    updateStep(pathwayID, step, Config.Step.Status.completed);
    setSubmission(1);
  }

  if (hasError) {
    return <BrokenStep text={errorText}/>
  }

  if (submission) {
    return <QuizSummary summaryHTML={summaryHTML}/>;
  }

  return (
    <div className='pathway-step__details'>
      <div className='pathway-step__content mb-0'>
        <section className='quiz-section'>
          <Preview
            loaded={loaded}
            config={config}
            clickHandler={doSetLoaded}
          />
          <QuizForm
            loaded={loaded}
            formId={formId}
            pathwayID={pathwayID}
            step={step}
            completeCallback={completeStep}
            submission={submission}
            setHasError={setHasError}
            setErrorText={setErrorText}
            survey={survey}
          />
        </section>
      </div>
    </div>
  );
}
